import React from 'react';

const StatsCard = ({ users, activeFilter, setActiveFilter }) => {
  const getValidRoles = (user) => {
    if (!user.roles) return [];
    return user.roles.filter(r => r.duty && r.duty.toLowerCase() !== 'none');
  };

  const totalMembers = users.length;
  const totalPositions = users.reduce((acc, user) => acc + getValidRoles(user).length, 0);

  const officers = users.filter(u => getValidRoles(u).length > 0);
  const totalOfficers = officers.length;

  const activeOfficers = officers.filter(u => !u.status || u.status === 'Active').length;
  const inactiveOfficers = officers.filter(u => u.status === 'Inactive').length;

  const buklodCount = users.filter(u => u.category === 'Buklod').length;
  const kadiwaCount = users.filter(u => u.category === 'Kadiwa').length;
  const binhiCount = users.filter(u => u.category === 'Binhi').length;

  const roleBreakdown = users.reduce((acc, user) => {
    getValidRoles(user).forEach(role => {
      const dutyName = role.duty.toUpperCase(); 
      acc[dutyName] = (acc[dutyName] || 0) + 1;
    });
    return acc;
  }, {});

  // Visual helper to show what is clickable and currently active
  const getClickableStyle = (filterName) => {
    const isActive = activeFilter === filterName;
    const isGlobalAll = activeFilter === 'All';
    return {
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      transform: isActive ? 'scale(1.02)' : 'scale(1)',
      opacity: isActive || isGlobalAll ? 1 : 0.4,
      boxShadow: isActive ? '0 4px 12px rgba(14, 165, 233, 0.15)' : 'none',
      border: isActive ? '1px solid var(--primary)' : '1px solid transparent',
      borderRadius: '8px'
    };
  };

  const handleFilterClick = (filterName) => {
    // Toggle off if clicking the already active filter
    setActiveFilter(activeFilter === filterName ? 'All' : filterName);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '2.5rem' }}>
      
      {/* TOP ROW: Primary Statistics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '1.5rem' }}>
        
        {/* Total Members (Clicking this resets to All) */}
        <div 
          className="card" 
          onClick={() => handleFilterClick('All')}
          style={{ ...getClickableStyle('All'), textAlign: 'center', padding: '1.5rem 1rem', border: activeFilter === 'All' ? '1px solid var(--primary)' : 'none' }}
        >
          <h4 style={{ color: 'var(--text-muted)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Total Members</h4>
          <h2 style={{ fontSize: '2.2rem', color: 'var(--primary)', fontWeight: 800, margin: 0 }}>{totalMembers}</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '0.25rem' }}>Serial Number</p>
        </div>

        {/* Total Positions (Static/Informational - Doesn't filter users well since users have multiple roles) */}
        <div className="card" style={{ textAlign: 'center', padding: '1.5rem 1rem', opacity: activeFilter === 'All' ? 1 : 0.6 }}>
          <h4 style={{ color: 'var(--text-muted)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Total Positions</h4>
          <h2 style={{ fontSize: '2.2rem', color: 'var(--secondary)', fontWeight: 800, margin: 0 }}>{totalPositions}</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '0.25rem' }}>All Valid Roles</p>
        </div>

        {/* Total Officers */}
        <div 
          className="card" 
          onClick={() => handleFilterClick('Officers')}
          style={{ ...getClickableStyle('Officers'), textAlign: 'center', padding: '1.5rem 1rem' }}
        >
          <h4 style={{ color: 'var(--text-muted)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Total Officers</h4>
          <h2 style={{ fontSize: '2.2rem', color: 'var(--primary)', fontWeight: 800, margin: 0 }}>{totalOfficers}</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '0.25rem' }}>Per Head</p>
        </div>

        {/* Officer Status */}
        <div className="card" style={{ textAlign: 'center', padding: '1.5rem 1rem' }}>
          <h4 style={{ color: 'var(--text-muted)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1rem' }}>Officer Status</h4>
          <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
              <div 
                onClick={() => handleFilterClick('Active')}
                style={{ ...getClickableStyle('Active'), padding: '5px 10px', textAlign: 'center' }}
              >
                  <h2 style={{ fontSize: '1.6rem', color: 'var(--primary)', fontWeight: 800, margin: 0 }}>{activeOfficers}</h2>
                  <div style={{ fontSize: '0.65rem', color: 'var(--primary)', fontWeight: 700, textTransform: 'uppercase' }}>Active</div>
              </div>
              <div style={{ width: '1px', height: '30px', background: 'var(--border)' }}></div>
              <div 
                onClick={() => handleFilterClick('Inactive')}
                style={{ ...getClickableStyle('Inactive'), padding: '5px 10px', textAlign: 'center' }}
              >
                  <h2 style={{ fontSize: '1.6rem', color: 'var(--text-muted)', fontWeight: 800, margin: 0 }}>{inactiveOfficers}</h2>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase' }}>Inactive</div>
              </div>
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="card" style={{ textAlign: 'center', padding: '1.5rem 1rem' }}>
          <h4 style={{ color: 'var(--text-muted)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1rem' }}>Category Breakdown</h4>
          <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
              <div 
                onClick={() => handleFilterClick('Buklod')}
                style={{ ...getClickableStyle('Buklod'), padding: '5px 10px', textAlign: 'center' }}
              >
                  <h2 style={{ fontSize: '1.6rem', color: '#dc2626', fontWeight: 800, margin: 0 }}>{buklodCount}</h2>
                  <div style={{ fontSize: '0.65rem', color: '#dc2626', fontWeight: 700, textTransform: 'uppercase' }}>Buklod</div>
              </div>
              <div 
                onClick={() => handleFilterClick('Kadiwa')}
                style={{ ...getClickableStyle('Kadiwa'), padding: '5px 10px', textAlign: 'center' }}
              >
                  <h2 style={{ fontSize: '1.6rem', color: 'var(--primary)', fontWeight: 800, margin: 0 }}>{kadiwaCount}</h2>
                  <div style={{ fontSize: '0.65rem', color: 'var(--primary)', fontWeight: 700, textTransform: 'uppercase' }}>Kadiwa</div>
              </div>
              <div 
                onClick={() => handleFilterClick('Binhi')}
                style={{ ...getClickableStyle('Binhi'), padding: '5px 10px', textAlign: 'center' }}
              >
                  <h2 style={{ fontSize: '1.6rem', color: '#10b981', fontWeight: 800, margin: 0 }}>{binhiCount}</h2>
                  <div style={{ fontSize: '0.65rem', color: '#10b981', fontWeight: 700, textTransform: 'uppercase' }}>Binhi</div>
              </div>
          </div>
        </div>

      </div>

      {/* BOTTOM ROW: Role Distribution (Full Width) */}
      <div className="card" style={{ padding: '1.5rem' }}>
        <h4 style={{ color: 'var(--text-muted)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1rem', textAlign: 'center' }}>Role Distribution</h4>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
          {Object.entries(roleBreakdown).map(([role, count]) => (
            <div 
              key={role} 
              onClick={() => handleFilterClick(role)}
              className="role-chip" 
              style={{ 
                ...getClickableStyle(role),
                margin: 0, 
                fontSize: '0.8rem', 
                backgroundColor: activeFilter === role ? 'var(--primary)' : 'var(--chip-bg)', 
                color: activeFilter === role ? 'white' : 'var(--chip-text)' 
              }}
            >
              <span>{role}:</span>&nbsp;<strong>{count}</strong>
            </div>
          ))}
          {Object.keys(roleBreakdown).length === 0 && <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>No roles assigned yet.</span>}
        </div>
      </div>

    </div>
  );
};

export default StatsCard;